import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {logoRES} from "./logoBase";

/**
 * Dodaje logo w nagłówku każdej strony
 */
const addLogoToPage = (pdf, logoImage, margin = 10) => {
    const logoWidth = 70;
    const logoHeight = 15;
    pdf.addImage(logoImage, 'PNG', margin, margin, logoWidth, logoHeight);
};

/**
 * Dodaje napis na określonej pozycji Y
 */
const addOrderNameToPage = (pdf, officialOrderName, margin = 10, textY) => {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.text(`Official order name: ${officialOrderName}`, margin, textY);
};

/**
 * Alternatywna metoda: Odczytuje elementy legendy z prostych div'ów
 * Format: <div>A : 200 (29.1%) Obecność i poprawność barcode</div>
 */
const extractLegendItemsAlternative = (chartElement) => {
    const legendItems = [];
    const seenTexts = new Set();

    // Szukaj wszystkich div'ów w chartElement
    const allDivs = chartElement.querySelectorAll('div');

    allDivs.forEach(div => {
        const text = div.textContent.trim();

        // Wzór: "X : Y (Z%)" gdzie X to litera/liczba, Y to liczba, Z to procent
        const pattern = /^[A-Za-z0-9]+\s*:\s*\d+\s*\(\d+\.?\d*%\)/;

        // Sprawdź ile razy pojawia się wzór w tym elemencie
        const matches = text.match(/[A-Za-z0-9]+\s*:\s*\d+\s*\(\d+\.?\d*%\)/g);
        const matchCount = matches ? matches.length : 0;

        // Tylko zaakceptuj jeśli:
        // 1. Tekst zaczyna się wzorem
        // 2. Zawiera DOKŁADNIE JEDEN wzór (nie kilka złeponych)
        // 3. Tekst nie jest już znany (deduplikacja)
        // 4. Tekst ma rozsądną długość (< 200 znaków - pojedynczy element)
        if (pattern.test(text) && matchCount === 1 && !seenTexts.has(text) && text.length < 200) {
            const color = extractColorFromElement(div);

            legendItems.push({
                color: color,
                label: text
            });
            seenTexts.add(text);
        }
    });

    return legendItems;
};

/**
 * Ekstrahuje kolor z elementu DOM
 */
const extractColorFromElement = (element) => {
    // 1. Sprawdź inline style
    const style = element.getAttribute('style');
    if (style) {
        const bgMatch = style.match(/background-color:\s*([^;]+)/);
        if (bgMatch) {
            return bgMatch[1].trim();
        }
    }

    // 2. Sprawdź computed style
    const computedStyle = window.getComputedStyle(element);
    const bgColor = computedStyle.backgroundColor;
    if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
        return bgColor;
    }

    // 3. Szukaj poprzedzającego koloru (color indicator)
    const parent = element.parentElement;
    if (parent) {
        const prevElement = element.previousElementSibling;
        if (prevElement) {
            const prevStyle = window.getComputedStyle(prevElement);
            const prevBgColor = prevStyle.backgroundColor;
            if (prevBgColor && prevBgColor !== 'rgba(0, 0, 0, 0)' && prevBgColor !== 'transparent') {
                return prevBgColor;
            }
        }
    }

    // Default fallback
    return 'rgb(0, 0, 0)';
};

/**
 * Metoda 1: Odczytuje elementy legendy z flex layout'u
 * Format: <div style="display: flex"><div style="background-color: ..."></div><span>Tekst</span></div>
 */
const extractLegendItems = (chartElement) => {
    const legendItems = [];
    const seenTexts = new Set();

    // Szukaj wszystkich div'ów które zawierają legendę (struktura: color box + text)
    const legendContainers = chartElement.querySelectorAll('div[style*="display: flex"]');

    legendContainers.forEach(container => {
        // Szukaj wewnątrz color box'a (mały kwadrat z kolorem)
        const colorBox = container.querySelector('div[style*="background-color"]');
        const textSpan = container.querySelector('span');

        if (colorBox && textSpan) {
            const text = textSpan.textContent.trim();
            if (text.length > 0 && !seenTexts.has(text)) { // Sprawdź czy tekst już istnieje
                // Ekstrahuj kolor z inline style
                const styleAttr = colorBox.getAttribute('style');
                const colorMatch = styleAttr.match(/background-color:\s*([^;]+)/);
                const color = colorMatch ? colorMatch[1].trim() : 'rgb(0, 0, 0)';

                legendItems.push({
                    color,
                    label: text
                });
                seenTexts.add(text);
            }
        }
    });

    return legendItems;
};
const addLegendPageToPDF = async (pdf, legendItems, margin = 10) => {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const logoHeight = 15;
    const logoTopMargin = margin + logoHeight + 2;

    // ✅ UTWÓRZ HTML legendy
    const legendHTML = document.createElement('div');
    legendHTML.style.cssText = `
        position: fixed;
        left: -9999px;
        top: -9999px;
        width: 800px;
        padding: 20px;
        backgroundColor: white;
        fontFamily: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif;
        fontSize: 13px;
        color: rgb(52, 71, 103);
    `;

    // Tytuł
    const titleDiv = document.createElement('h2');
    titleDiv.style.cssText = `
        margin: 0 0 16px 0;
        fontSize: 14px;
        fontWeight: bold;
        color: rgb(52, 71, 103);
    `;
    titleDiv.textContent = 'Legenda';
    legendHTML.appendChild(titleDiv);

    // Elementy legendy
    legendItems.forEach((item) => {
        const itemDiv = document.createElement('div');
        itemDiv.style.cssText = `
            display: flex;
            alignItems: center;
            gap: 10px;
            marginBottom: 8px;
            fontSize: 13px;
        `;

        // Kolorowy kwadrat
        const colorBox = document.createElement('div');
        colorBox.style.cssText = `
            width: 12px;
            height: 12px;
            backgroundColor: ${item.color};
            borderRadius: 2px;
            flexShrink: 0;
        `;

        // Tekst
        const textSpan = document.createElement('span');
        textSpan.style.cssText = `
            color: rgb(52, 71, 103);
            fontWeight: 500;
        `;
        textSpan.textContent = item.label;

        itemDiv.appendChild(colorBox);
        itemDiv.appendChild(textSpan);
        legendHTML.appendChild(itemDiv);

    });

    // Dodaj do DOM tymczasowo
    document.body.appendChild(legendHTML);

    // Czekaj na renderowanie
    await new Promise(resolve => setTimeout(resolve, 500));

    // ✅ PRZECHWYCENIE HTML'a jako canvas
    const canvasLegend = await html2canvas(legendHTML, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        timeout: 15000
    });


    // Usuń z DOM
    document.body.removeChild(legendHTML);

    // ✅ DODAJ DO PDF
    pdf.addPage();
    addLogoToPage(pdf, logoRES, margin);

    const imgDataLegend = canvasLegend.toDataURL('image/jpeg', 0.75);

    // Skalowanie dla PDF
    const legendMargin = margin;
    let legendWidth = pageWidth - (legendMargin * 2);
    let legendHeight = (canvasLegend.height * legendWidth) / canvasLegend.width;

    const maxHeight = pageHeight - logoTopMargin - margin;
    if (legendHeight > maxHeight) {
        legendHeight = maxHeight;
        legendWidth = (canvasLegend.width * legendHeight) / canvasLegend.height;
    }

    const xPosition = legendMargin;
    const yPosition = logoTopMargin;

    pdf.addImage(
        imgDataLegend,
        'JPEG',
        xPosition,
        yPosition,
        legendWidth,
        legendHeight
    );

};

// Funkcja do pobierania pojedynczego wykresu - ASYNC!
export const downloadChartPDFBar = async (chartId, chartTitle = 'chart', officialOrderName = '', isFullscreen = false) => {
    try {
        let chartElement;

        if (isFullscreen) {
            // Szukaj wykresu w fullscreen containerze
            const fullscreenContainer = document.querySelector('div[data-fullscreen-content]');
            if (fullscreenContainer) {
                chartElement = fullscreenContainer.querySelector(`#${chartId}`);
            }
        } else {
            // Normalny selektor
            chartElement = document.getElementById(chartId);
        }

        if (!chartElement) {
            return;
        }

        // Ukryj span'y z "Download"
        const spans = chartElement.querySelectorAll('span');
        const hiddenSpans = [];

        spans.forEach(span => {
            if (span.textContent.toLowerCase().includes('download')) {
                let parent = span.parentElement;
                if (parent) {
                    const originalDisplay = parent.style.display;
                    parent.style.display = 'none';
                    hiddenSpans.push({ element: parent, originalDisplay });
                }
            }
        });

        // STRONA 1: Przechwycenie wykresu
        const canvasChart = await html2canvas(chartElement, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
            timeout: 15000
        });

        const dataSize = (canvasChart.toDataURL('image/jpeg', 0.75).length / 1024).toFixed(2);

        // Przywróć oryginalne style na stronie (ukryty Download)
        hiddenSpans.forEach(({ element, originalDisplay }) => {
            element.style.display = originalDisplay;
        });

        const imgDataChart = canvasChart.toDataURL('image/jpeg', 0.75);

        const pdf = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4',
            compress: true
        });


        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        const bottomMargin = 10;
        const logoHeight = 15;
        const textHeight = 6;
        const logoTopMargin = margin + logoHeight + 2;
        const availableHeightPerPage = pageHeight - logoTopMargin - bottomMargin;

        // --- STRONA 1: WYKRES ---
        addLogoToPage(pdf, logoRES, margin);

        // ✅ SKALOWANIE WYKRESU Z ROZRÓŻNIENIEM FULLSCREEN
        const chartMargin = 5;
        let imgWidth;
        let imgHeight;

        if (isFullscreen) {
            // 🖥️ FULLSCREEN: Rozciągnij na całą dostępną powierzchnię (bez zachowywania proporcji)
            imgWidth = pageWidth - (chartMargin * 2);
            imgHeight = availableHeightPerPage - 15; // Prawie cała dostępna wysokość
        } else {
            // 📄 NORMALNY WIDOK: Zachowaj proporcje
            imgWidth = pageWidth - (chartMargin * 2);
            imgHeight = (canvasChart.height * imgWidth) / canvasChart.width;

            // Jeśli za wysoki - zmniejsz proportionally
            const maxHeight = availableHeightPerPage - 15;
            if (imgHeight > maxHeight) {
                imgHeight = maxHeight;
                imgWidth = (canvasChart.width * imgHeight) / canvasChart.height;
            }
        }

        // Dodaj wykres na pierwszej stronie
        let xPosition = chartMargin;
        if (!isFullscreen) {
            // Wyśrodkuj poziomo gdy nie jest fullscreen
            xPosition = (pageWidth - imgWidth) / 2;
        }
        let currentChartY = logoTopMargin;

        if (officialOrderName) {
            const orderNameY = logoTopMargin + 3;
            addOrderNameToPage(pdf, officialOrderName, margin, orderNameY);
            currentChartY = orderNameY + textHeight + 2;
        }

        pdf.addImage(
            imgDataChart,
            'JPEG',
            xPosition,
            currentChartY,
            imgWidth,
            imgHeight
        );


        // Spróbuj metodę 1: Flex layout z color box'ami
        let legendItems = extractLegendItems(chartElement);

        // Fallback do metody 2: Proste div'y z tekstem
        if (legendItems.length === 0) {
            legendItems = extractLegendItemsAlternative(chartElement);
        }

        legendItems.forEach((item, index) => {
            const hasPolishChars = /[ąćęłńóśźż]/i.test(item.label);
            const polishIndicator = hasPolishChars ? '🇵🇱' : '';
        });

        if (legendItems.length > 0) {
            // ✅ ASYNC - czekaj na dodanie legendy
            await addLegendPageToPDF(pdf, legendItems, margin);
        }

        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `${chartTitle}-${timestamp}.pdf`;

        pdf.save(filename);


    } catch (error) {
        console.error('%c❌ Error generating PDF:', 'color: red; font-weight: bold;', error);
        console.error('%c📍 Stack trace:', 'color: red;', error.stack);
    }
};